import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Chip,
  IconButton
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import api from '../../../services/api'

const ICDMasterForm = ({ open, onClose, onSuccess, editData = null }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [loading, setLoading] = useState(false)
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    shortDescription: '',
    icdVersion: 'ICD-10',
    chapter: '',
    chapterCode: '',
    chapterDescription: '',
    block: '',
    blockCode: '',
    blockDescription: '',
    category: '',
    subCategory: '',
    clinicalDescription: '',
    diagnosticCriteria: '',
    synonyms: [],
    icd10: {
      isBillable: true,
      isManifestation: false,
      includes: [],
      excludes1: [],
      excludes2: [],
      codeFirst: [],
      useAdditionalCode: [],
      ageSexRestriction: '',
      laterality: ''
    },
    icd11: {
      foundationUri: '',
      linearizationUri: '',
      isResidual: false,
      isExtensionCode: false,
      stemId: '',
      severity: '',
      temporal: '',
      anatomy: ''
    },
    icd10pcs: {
      section: '',
      bodySystem: '',
      rootOperation: '',
      bodyPart: '',
      approach: '',
      device: '',
      qualifier: ''
    },
    ccsCategory: '',
    hccCode: '',
    rvu: '',
    usageCount: 0,
    isHAC: false,
    isPSI: false,
    isPOAExempt: false,
    isActive: true
  })

  const [newSynonym, setNewSynonym] = useState('')
  const [newInclude, setNewInclude] = useState('')
  const [newExclude1, setNewExclude1] = useState('')
  const [newCodeFirst, setNewCodeFirst] = useState('')
  const [newUseAdditional, setNewUseAdditional] = useState('')

  React.useEffect(() => {
    if (editData) {
      setFormData(editData)
    }
  }, [editData])

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const addToArray = (arrayField, value, setValue) => {
    if (value.trim()) {
      if (arrayField.includes('.')) {
        const [parent, child] = arrayField.split('.')
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: [...prev[parent][child], value.trim()]
          }
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          [arrayField]: [...prev[arrayField], value.trim()]
        }))
      }
      setValue('')
    }
  }

  const removeFromArray = (arrayField, index) => {
    if (arrayField.includes('.')) {
      const [parent, child] = arrayField.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: prev[parent][child].filter((_, i) => i !== index)
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [arrayField]: prev[arrayField].filter((_, i) => i !== index)
      }))
    }
  }

  const handleSubmit = async () => {
    try {
      setLoading(true)

      // Validate required fields
      if (!formData.code || !formData.description || !formData.icdVersion) {
        enqueueSnackbar('Code, description, and ICD version are required', { variant: 'error' })
        return
      }

      const endpoint = editData 
        ? `/api/v1/master/icd/${editData._id}`
        : '/api/v1/master/icd'
      
      const method = editData ? 'put' : 'post'

      const response = await api[method](endpoint, formData)

      if (response.data.success) {
        enqueueSnackbar(
          editData ? 'ICD code updated successfully' : 'ICD code created successfully',
          { variant: 'success' }
        )
        onSuccess()
        onClose()
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to save ICD code',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      code: '',
      description: '',
      shortDescription: '',
      icdVersion: 'ICD-10',
      chapter: '',
      chapterCode: '',
      chapterDescription: '',
      block: '',
      blockCode: '',
      blockDescription: '',
      category: '',
      subCategory: '',
      clinicalDescription: '',
      synonyms: [],
      icd10: {
        isBillable: true,
        isManifestation: false,
        includes: [],
        excludes1: [],
        excludes2: [],
        laterality: ''
      },
      ccsCategory: '',
      hccCode: '',
      rvu: '',
      isHAC: false,
      isPSI: false,
      isPOAExempt: false,
      isActive: true
    })
    setNewSynonym('')
    setNewInclude('')
    setNewExclude1('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {editData ? 'Edit ICD Code' : 'Add New ICD Code'}
        </Typography>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="ICD Code *"
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
              placeholder="e.g., I10"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>ICD Version *</InputLabel>
              <Select
                value={formData.icdVersion}
                label="ICD Version *"
                onChange={(e) => handleInputChange('icdVersion', e.target.value)}
              >
                <MenuItem value="ICD-10">ICD-10</MenuItem>
                <MenuItem value="ICD-11">ICD-11</MenuItem>
                <MenuItem value="ICD-10-PCS">ICD-10-PCS</MenuItem>
                <MenuItem value="ICD-9-CM">ICD-9-CM</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Description *"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Full ICD description"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Short Description"
              value={formData.shortDescription}
              onChange={(e) => handleInputChange('shortDescription', e.target.value)}
              placeholder="Brief description"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Clinical Description"
              value={formData.clinicalDescription}
              onChange={(e) => handleInputChange('clinicalDescription', e.target.value)}
              placeholder="Detailed clinical description"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Diagnostic Criteria"
              value={formData.diagnosticCriteria}
              onChange={(e) => handleInputChange('diagnosticCriteria', e.target.value)}
              placeholder="Diagnostic criteria and guidelines"
            />
          </Grid>

          {/* Hierarchy */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Hierarchy</Typography>
          </Grid>
          
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="Chapter"
              value={formData.chapter}
              onChange={(e) => handleInputChange('chapter', e.target.value)}
              placeholder="e.g., IX"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Chapter Code"
              value={formData.chapterCode}
              onChange={(e) => handleInputChange('chapterCode', e.target.value)}
              placeholder="e.g., I00-I99"
            />
          </Grid>
          
          <Grid item xs={12} md={7}>
            <TextField
              fullWidth
              label="Chapter Description"
              value={formData.chapterDescription}
              onChange={(e) => handleInputChange('chapterDescription', e.target.value)}
              placeholder="e.g., Diseases of the circulatory system"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Block"
              value={formData.block}
              onChange={(e) => handleInputChange('block', e.target.value)}
              placeholder="e.g., I10-I16"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={(e) => handleInputChange('category', e.target.value)}
              placeholder="Category code"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Sub Category"
              value={formData.subCategory}
              onChange={(e) => handleInputChange('subCategory', e.target.value)}
              placeholder="Sub category"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Block Code"
              value={formData.blockCode}
              onChange={(e) => handleInputChange('blockCode', e.target.value)}
              placeholder="e.g., I10-I16"
            />
          </Grid>
          
          <Grid item xs={12} md={9}>
            <TextField
              fullWidth
              label="Block Description"
              value={formData.blockDescription}
              onChange={(e) => handleInputChange('blockDescription', e.target.value)}
              placeholder="e.g., Hypertensive diseases"
            />
          </Grid>

          {/* ICD-10 Specific */}
          {formData.icdVersion === 'ICD-10' && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>ICD-10 Specific</Typography>
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.icd10.isBillable}
                      onChange={(e) => handleInputChange('icd10.isBillable', e.target.checked)}
                    />
                  }
                  label="Billable"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.icd10.isManifestation}
                      onChange={(e) => handleInputChange('icd10.isManifestation', e.target.checked)}
                    />
                  }
                  label="Manifestation"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Laterality</InputLabel>
                  <Select
                    value={formData.icd10.laterality}
                    label="Laterality"
                    onChange={(e) => handleInputChange('icd10.laterality', e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="RIGHT">Right</MenuItem>
                    <MenuItem value="LEFT">Left</MenuItem>
                    <MenuItem value="BILATERAL">Bilateral</MenuItem>
                    <MenuItem value="UNSPECIFIED">Unspecified</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Age/Sex Restriction"
                  value={formData.icd10.ageSexRestriction}
                  onChange={(e) => handleInputChange('icd10.ageSexRestriction', e.target.value)}
                  placeholder="e.g., Adults only, Females only"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Code First Instructions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    fullWidth
                    label="Add Code First"
                    value={newCodeFirst || ''}
                    onChange={(e) => setNewCodeFirst(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addToArray('icd10.codeFirst', newCodeFirst, setNewCodeFirst)
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => addToArray('icd10.codeFirst', newCodeFirst, setNewCodeFirst)}
                    startIcon={<Add />}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData?.icd10?.codeFirst?.map((code, index) => (
                    <Chip
                      key={index}
                      label={code}
                      onDelete={() => removeFromArray('icd10.codeFirst', index)}
                      size="small"
                    />
                  ))}
                </Box>
              </Grid>
            </>
          )}

          {/* ICD-11 Specific */}
          {formData.icdVersion === 'ICD-11' && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>ICD-11 Specific</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Foundation URI"
                  value={formData.icd11.foundationUri}
                  onChange={(e) => handleInputChange('icd11.foundationUri', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Linearization URI"
                  value={formData.icd11.linearizationUri}
                  onChange={(e) => handleInputChange('icd11.linearizationUri', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.icd11.isResidual}
                      onChange={(e) => handleInputChange('icd11.isResidual', e.target.checked)}
                    />
                  }
                  label="Residual"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.icd11.isExtensionCode}
                      onChange={(e) => handleInputChange('icd11.isExtensionCode', e.target.checked)}
                    />
                  }
                  label="Extension Code"
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Stem ID"
                  value={formData.icd11.stemId}
                  onChange={(e) => handleInputChange('icd11.stemId', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Severity"
                  value={formData.icd11.severity}
                  onChange={(e) => handleInputChange('icd11.severity', e.target.value)}
                />
              </Grid>
            </>
          )}

          {/* ICD-10-PCS Specific */}
          {formData.icdVersion === 'ICD-10-PCS' && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>ICD-10-PCS Specific</Typography>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Section"
                  value={formData.icd10pcs.section}
                  onChange={(e) => handleInputChange('icd10pcs.section', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Body System"
                  value={formData.icd10pcs.bodySystem}
                  onChange={(e) => handleInputChange('icd10pcs.bodySystem', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Root Operation"
                  value={formData.icd10pcs.rootOperation}
                  onChange={(e) => handleInputChange('icd10pcs.rootOperation', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Body Part"
                  value={formData.icd10pcs.bodyPart}
                  onChange={(e) => handleInputChange('icd10pcs.bodyPart', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Approach"
                  value={formData.icd10pcs.approach}
                  onChange={(e) => handleInputChange('icd10pcs.approach', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Device"
                  value={formData.icd10pcs.device}
                  onChange={(e) => handleInputChange('icd10pcs.device', e.target.value)}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Qualifier"
                  value={formData.icd10pcs.qualifier}
                  onChange={(e) => handleInputChange('icd10pcs.qualifier', e.target.value)}
                />
              </Grid>
            </>
          )}

          {/* Insurance & Billing */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Insurance & Billing</Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="CCS Category"
              value={formData.ccsCategory}
              onChange={(e) => handleInputChange('ccsCategory', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="HCC Code"
              value={formData.hccCode}
              onChange={(e) => handleInputChange('hccCode', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label="RVU"
              value={formData.rvu}
              onChange={(e) => handleInputChange('rvu', parseFloat(e.target.value) || 0)}
            />
          </Grid>

          {/* Synonyms */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Synonyms</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                label="Add Synonym"
                value={newSynonym}
                onChange={(e) => setNewSynonym(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    addToArray('synonyms', newSynonym, setNewSynonym)
                  }
                }}
              />
              <Button
                variant="outlined"
                onClick={() => addToArray('synonyms', newSynonym, setNewSynonym)}
                startIcon={<Add />}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.synonyms.map((synonym, index) => (
                <Chip
                  key={index}
                  label={synonym}
                  onDelete={() => removeFromArray('synonyms', index)}
                  size="small"
                />
              ))}
            </Box>
          </Grid>

          {/* Quality Indicators */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Quality Indicators</Typography>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isHAC}
                  onChange={(e) => handleInputChange('isHAC', e.target.checked)}
                />
              }
              label="Hospital Acquired Condition (HAC)"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isPSI}
                  onChange={(e) => handleInputChange('isPSI', e.target.checked)}
                />
              }
              label="Patient Safety Indicator (PSI)"
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isPOAExempt}
                  onChange={(e) => handleInputChange('isPOAExempt', e.target.checked)}
                />
              }
              label="POA Exempt"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : (editData ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ICDMasterForm